import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "./electricalApi";
import ElectricalNewAudit from "./ElectricalNewAudit";
import "./ElectricalCapture.css";

/**
 * My electrical audits - the landing screen for the new Electrical module.
 *
 * Modeled directly on Csa/CsaAuditList.js: "New audit" sits at the top, the
 * list of audits (with status/search filters) comes below it. See the
 * CSA-parity analysis for why this module is being rebuilt on CSA's pattern
 * instead of the existing modal-based ReportModal/PreviewReportModal flow.
 */

const FILTERS = [
  { key: "", label: "All" },
  { key: "draft", label: "In progress" },
  { key: "submitted", label: "Submitted" },
  { key: "reviewed", label: "Reviewed" },
];

const RELATION_LABEL = {
  auditor: "Yours",
  contributor: "Shared with you",
  reviewer: "To review",
  scheduler: "You scheduled it",
  manager: "Team",
};

/**
 * Used in two places, which is why it takes `embedded` - same reasoning as
 * CsaAuditList:
 *
 *   /electrical-audits   on its own, owning the viewport
 *   Home, sidebar        inside a panel that is already `height: 90vh`
 *
 * `allData` is Home's already-fetched master checklist, passed through only
 * when embedded. When we have it, "New audit" opens inline instead of
 * navigating to the standalone /electrical-new route, so that screen can
 * reuse this data instead of fetching its own copy.
 */
export default function ElectricalAuditList({ embedded, allData }) {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [discarding, setDiscarding] = useState(null);
  const [showNewAudit, setShowNewAudit] = useState(false);
  const timer = useRef(null);

  const load = useCallback(
    (nextStatus, nextQ) => {
      setBusy(true);
      const params = [];
      if (nextStatus) params.push(`status=${encodeURIComponent(nextStatus)}`);
      if (nextQ && nextQ.trim()) params.push(`q=${encodeURIComponent(nextQ.trim())}`);
      api
        .listAudits(params.join("&"))
        .then((d) => {
          // console.log(d);
          setData(d);
          setError(null);
        })
        .catch((e) => setError(e.message))
        .finally(() => setBusy(false));
    },
    []
  );

  useEffect(() => {
    load(status, q);
    // The search box is debounced; the filter tabs are not, because a tap is
    // already a deliberate act and waiting 400ms for it feels broken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const search = (e) => {
    const v = e.target.value;
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(status, v), 400);
  };

  const openNewAudit = () => {
    if (allData) {
      // Reuse Home's already-fetched checklist - stay on this screen instead
      // of navigating, so ElectricalNewAudit never has to fetch its own copy.
      setShowNewAudit(true);
    } else {
      navigate("/electrical-new");
    }
  };

  if (showNewAudit) {
    return <ElectricalNewAudit onCancel={() => setShowNewAudit(false)} />;
  }
  // console.log('==');
  // console.log('data: ',data);
  return (
    <div className={"elec-capture elec-list-page" + (embedded ? "" : " elec-screen")}>
      <div className="cap-writeup" style={{ maxWidth: 900 }}>
        <div className="wu-head">
          {/*
            This route is standalone - reached from the sidebar but rendered
            outside it - so without this there is no way back to the rest of
            the application except the browser's own button.
          */}
          {!embedded ? (
            <button
              type="button"
              className="cap-back dark"
              title="Back to the dashboard"
              onClick={() => navigate("/home")}
            >
              ‹
            </button>
          ) : null}
          <div>
            <h2>Electrical audits</h2>
            <p>Pick up where you left off, or start a new one.</p>
          </div>
          <button type="button" className="cap-btn" onClick={openNewAudit}>
            New audit
          </button>
        </div>

        <div className="list-controls">
          <div className="list-tabs">
            {FILTERS.map((f) => (
              <button
                key={f.key || "all"}
                type="button"
                className={"list-tab" + (status === f.key ? " on" : "")}
                onClick={() => setStatus(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            className="list-search"
            value={q}
            placeholder="Search client, site or report id"
            onChange={search}
          />
        </div>

        {error ? <p className="wu-error">{error}</p> : null}

        {!data && !error ? <div className="cap-loading">Loading…</div> : null}

        {data && data?.audits?.length === 0 ? (
          <div className="wu-empty">
            <h2>{q || status ? "Nothing matches" : "No audits yet"}</h2>
            <p>
              {q || status
                ? "Try a different search, or clear the filter."
                : "Start one and it will appear here. Everything saves as you go, so you can stop and come back."}
            </p>
          </div>
        ) : null}

        {data
          ? data?.audits?.map((a) => (
              <AuditCard
                key={a.id}
                audit={a}
                onOpen={() => navigate(`/electrical-audit/${a.id}`)}
                onDiscard={
                  // Drafts only, and only ones this account can edit - the
                  // server refuses anything else, but offering a button that
                  // will be refused is its own kind of wrong.
                  a.status === "draft" && a.can_edit ? () => setDiscarding(a) : null
                }
              />
            ))
          : null}

        {data && data.total > data?.audits?.length ? (
          <p className="list-more">
            Showing {data?.audits?.length} of {data?.total}. Narrow the search to find an older one.
          </p>
        ) : null}

        {busy && data ? <div className="cap-status saving">Loading…</div> : null}

        {discarding ? (
          <DiscardDialog
            audit={discarding}
            onCancel={() => setDiscarding(null)}
            onDone={() => {
              setDiscarding(null);
              load(status, q);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function AuditCard({ audit, onOpen, onDiscard }) {
  // The list API's own field is singular - "checkpoint", not "checkpoints" -
  // unlike every per-audit screen's own "checkpoints". Reading the plural
  // here always read undefined, which is what made the fallback text show
  // "answered/undefined" whenever there was no score yet to show instead.
  const pct = audit.checkpoint ? Math.round((audit.answered / audit.checkpoint) * 100) : 0;
  const isDraft = audit.status === "draft";
  /*
   * The same conformance score the header shows once the audit is open
   * (Yes/Partial/No weighted, N/A excluded) - not yet available while
   * nothing has been answered, so the plain completion count is kept as
   * the fallback for a brand new draft. Guarded against the literal
   * string "undefined"/"null" as well as a genuinely missing value - a
   * field that round-trips as that string rather than actually being
   * absent would otherwise pass a plain truthy/!= null check and print
   * as "undefined" right on the card.
   */
  const isUsable = (v) => v != null && v !== "undefined" && v !== "null" && v !== "";
  const hasScore = isUsable(audit.overall_pct) && !Number.isNaN(Number(audit.overall_pct));
  const band = isUsable(audit.maturity_band) ? audit.maturity_band : null;

  /*
   * A div, not a button, for the same reason as CSA's card: the discard
   * control is itself clickable and nesting one inside a <button> is invalid
   * HTML - browsers reparent it and the click lands in the wrong place.
   */
  return (
    <div
      className="list-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="list-card-main">
        <div className="list-card-top">
          <h3>{audit.organization || "—"}</h3>
          <span className={"list-chip s-" + audit.status}>{statusLabel(audit.status)}</span>
          {audit.relation && RELATION_LABEL[audit.relation] ? (
            <span className="list-rel">{RELATION_LABEL[audit.relation]}</span>
          ) : null}
        </div>

        <div className="list-card-sub">
          {audit.site_name} · {dateRange(audit.start_date, audit.end_date)}
        </div>

        {isDraft ? (
          <div className="list-progress" aria-label={`${audit.answered} of ${audit.checkpoint} answered`}>
            <div className="bar">
              <span style={{ width: (hasScore ? audit.overall_pct : pct) + "%" }} />
            </div>
            <span className="list-progress-n">
              {hasScore
                ? `${audit.overall_pct}% conformance${band ? " · " + band : ""}`
                : `${audit.answered}/${audit.checkpoint} answered`}
            </span>
          </div>
        ) : !hasScore ? (
          <div className="list-progress" aria-label={`${audit.answered} of ${audit.checkpoint} answered`}>
            <div className="bar">
              <span style={{ width: pct + "%" }} />
            </div>
            <span className="list-progress-n">
              {audit.answered}/{audit.checkpoint} answered
            </span>
          </div>
        ) : (
          <div className="list-score">
            <strong>{audit.overall_pct}%</strong>
            {band ? <span className="list-progress-n">{band}</span> : null}
          </div>
        )}
      </div>

      <div className="list-card-side">
        <span className="list-card-go">
          {isDraft && audit.can_edit ? "Continue ›" : "Open ›"}
        </span>
        {onDiscard ? (
          <span
            role="button"
            tabIndex={0}
            className="list-discard"
            title="Discard this draft"
            onClick={(e) => {
              // The whole card opens the audit; this must not do both.
              e.stopPropagation();
              onDiscard();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onDiscard();
              }
            }}
          >
            Discard
          </span>
        ) : null}
      </div>
    </div>
  );
}

function statusLabel(s) {
  if (s === "draft") return "In progress";
  if (s === "submitted") return "Submitted";
  if (s === "reviewed") return "Reviewed";
  return s;
}

function dateRange(a, b) {
  const fmt = (d) => {
    if (!d) return null;
    const x = new Date(d);
    if (isNaN(x.getTime())) return null;
    return x.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };
  const s = fmt(a);
  const e = fmt(b);
  if (s && e && s !== e) return `${s} – ${e}`;
  return s || e || "no dates";
}

/* ------------------------------------------------------------------ */

/**
 * Discarding is permanent, so the confirmation says what is actually at
 * stake rather than asking "are you sure?" about an unknown quantity.
 */
function DiscardDialog({ audit, onCancel, onDone }) {
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .previewDiscard(audit.id)
      .then(setPreview)
      .catch((e) => setError(e.message));
  }, [audit.id]);

  const hasWork = preview && !preview.is_empty;
  const ready = preview && (!hasWork || confirm.trim().toLowerCase() === "discard");

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.discardAudit(audit.id);
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="elec-pad-scrim" onClick={onCancel} />
      <div className="scope-dialog" role="dialog" aria-modal="true">
        <h3>Discard this draft?</h3>

        <p className="scope-what">
          <strong>{audit.organization}</strong>
          <span className="scope-count">{audit.site_name}</span>
        </p>

        {error ? <p className="wu-error">{error}</p> : null}

        {!preview ? (
          <p>Checking what this audit contains…</p>
        ) : preview.is_empty ? (
          <p>Nothing has been recorded on it yet. It will be removed for good.</p>
        ) : (
          <>
            <p>This cannot be undone. It would destroy:</p>
            <ul className="discard-list">
              {preview.answered ? <li>{preview.answered} answered checkpoints</li> : null}
              {preview.notes ? <li>{preview.notes} note{preview.notes === 1 ? "" : "s"}</li> : null}
              {preview.photos ? <li>{preview.photos} photo{preview.photos === 1 ? "" : "s"}</li> : null}
              {preview.extras ? (
                <li>{preview.extras} off-checklist observation{preview.extras === 1 ? "" : "s"}</li>
              ) : null}
              {preview.exclusions ? (
                <li>{preview.exclusions} scope exclusion{preview.exclusions === 1 ? "" : "s"}</li>
              ) : null}
            </ul>

            <label htmlFor="elec-discard-confirm">Type “discard” to confirm</label>
            <input
              id="elec-discard-confirm"
              type="text"
              autoComplete="off"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </>
        )}

        <div className="scope-actions">
          <button type="button" className="small-btn" onClick={onCancel}>
            Keep it
          </button>
          <button
            type="button"
            className="cap-btn danger"
            disabled={!ready || busy}
            onClick={go}
          >
            {busy ? "Discarding…" : "Discard"}
          </button>
        </div>
      </div>
    </>
  );
}
